import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthContextType {
  clientName: string | null;
  setClientName: (name: string | null) => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionId: string | null;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [clientName, setClientName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionId, setSessionId] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is already logged in from localStorage (persistent across tab closes)
    const storedClientName = localStorage.getItem('clientName');
    const storedSessionId = localStorage.getItem('sessionId');
    const currentTabSessionId = sessionStorage.getItem('currentSessionId');
    
    if (storedClientName && storedSessionId) {
      // If this tab has a different session ID, it means another tab logged in
      if (currentTabSessionId && currentTabSessionId !== storedSessionId) {
        console.log('Another tab has logged in, clearing this session');
        setClientName(null);
        setSessionId(null);
        sessionStorage.removeItem('currentSessionId');
      } else {
        // Restore the session
        setClientName(storedClientName);
        setSessionId(storedSessionId);
        // Set the current tab's session ID
        sessionStorage.setItem('currentSessionId', storedSessionId);
        console.log(`Session restored for ${storedClientName} with ID: ${storedSessionId}`);
      }
    }
    setIsLoading(false);
  }, []);

  const handleSetClientName = (name: string | null) => {
    setClientName(name);
    if (name) {
      const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);
      
      // Store in localStorage for persistence across tab closes
      localStorage.setItem('clientName', name);
      localStorage.setItem('sessionId', newSessionId);
      
      // Also store in sessionStorage for tab-specific tracking
      sessionStorage.setItem('currentSessionId', newSessionId);
      
      console.log(`New session created for ${name} with ID: ${newSessionId}`);
    } else {
      setSessionId(null);
      localStorage.removeItem('clientName');
      localStorage.removeItem('sessionId');
      sessionStorage.removeItem('currentSessionId');
    }
  };

  const logout = () => {
    console.log(`Logging out client: ${clientName}, session: ${sessionId}`);
    handleSetClientName(null);
  };

  const value = {
    clientName,
    setClientName: handleSetClientName,
    isAuthenticated: !!clientName,
    isLoading,
    sessionId,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 